import { Editor, Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Node } from 'prosemirror-model';
import uuidv4 from 'src/utils/uuidv4';

const updateItems = (
  items: TableOfContentsItem[],
  options: {
    editor: Editor;
    storage: TableOfContentsStorage;
    options: TableOfContentsOptions;
  },
) => {
  const {
    editor,
    storage,
    options: { onUpdate, anchorTypes },
    storage: { scrollPosition },
  } = options;

  const nodes: { node: Node; pos: number }[] = [];
  const activeIds: string[] = [];
  const activeItemId: string | null = null;

  editor.state.doc.descendants((node, pos) => {
    if (anchorTypes?.includes(node.type.name)) {
      nodes.push({ node, pos });
    }
  });

  nodes.forEach((node) => {
    const dom = editor.view.domAtPos(node.pos + 1).node as HTMLElement;
    if (scrollPosition >= dom.offsetTop) {
      activeIds.push(node.node.attrs['data-toc-id']);
    }
  });

  items = items.map((item) => ({
    ...item,
    isActive: item.id === activeItemId,
    isScrolledOver: activeIds.includes(item.id),
  }));

  if (onUpdate) {
    const isFirstUpdate = storage.content.length === 0;
    onUpdate(items, isFirstUpdate);
  }

  return items;
};

const updateTableOfContents = (options: {
  editor: Editor;
  storage: TableOfContentsStorage;
  options: TableOfContentsOptions;
  getLevel: any;
  getIndex: any;
}) => {
  const {
    options: { anchorTypes },
    storage: { scrollPosition, content },
    editor,
    getLevel,
    getIndex: _,
  } = options;

  const nodes: { node: Node; pos: number }[] = [];
  let items: TableOfContentsItem[] = [];
  const anchors: HTMLElement[] = [];

  editor.state.doc.descendants((node, pos) => {
    if (anchorTypes.includes(node.type.name)) {
      nodes.push({ node, pos });
    }
  });

  nodes.forEach((node, index) => {
    const dom = editor.view.domAtPos(node.pos + 1).node as HTMLElement;
    const isScrolledOver = scrollPosition >= dom.offsetTop;
    anchors.push(dom);

    const { level = getLevel?.(node.node, items) || 1 } = node.node.attrs;
    const prevNode = nodes[index - 1];
    const itemIndex = index + 1;
    const id = node.node.attrs['data-toc-id'];
    const { textContent } = node.node;
    const isActive = false;

    items.push({
      id,
      level,
      itemIndex,
      textContent,
      isActive,
      isScrolledOver: prevNode ? false : isScrolledOver,
      node: node.node,
      dom,
    });
  });

  items = updateItems(items, options);

  if (options.options.onUpdate) {
    const isFirstUpdate = content.length === 0;
    options.options.onUpdate(items, isFirstUpdate);
  }

  options.storage.anchors = anchors;
  options.storage.content = items;

  editor.state.tr.setMeta('toc', items);
  editor.view.dispatch(editor.state.tr);
};

export interface TableOfContentsItem {
  id: string;
  level: number;
  itemIndex: number;
  textContent: string;
  isActive: boolean;
  isScrolledOver: boolean;
  node: Node;
  dom: HTMLElement;
}

interface TableOfContentsStorage {
  content: TableOfContentsItem[];
  anchors: HTMLElement[];
  scrollHandler: () => void;
  scrollPosition: number;
}

interface TableOfContentsOptions {
  onUpdate: (items: TableOfContentsItem[], isFirstUpdate: boolean) => void;
  getId: (textContent: string) => string;
  scrollParent?: HTMLElement | Window;
  anchorTypes: string[];
  getIndex?: (node: Node, items: TableOfContentsItem[]) => number;
  getLevel?: (node: Node, items: TableOfContentsItem[]) => number;
}

const key = new PluginKey('tableOfContents');

const ProseMirrorPlugin = ({
  getId,
  anchorTypes = ['heading'],
}: Pick<TableOfContentsOptions, 'getId' | 'anchorTypes'>) =>
  new Plugin({
    key,
    appendTransaction(r, s, n) {
      const a = n.tr;
      let t = !1;
      if (r.some((transaction) => transaction.docChanged)) {
        const processedIds: string[] = [];
        n.doc.descendants((node, pos) => {
          const tocId = node.attrs['data-toc-id'];
          if (anchorTypes.includes(node.type.name)) {
            if (tocId == null || processedIds.includes(tocId)) {
              const newId = getId(node.textContent);

              a.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                'data-toc-id': newId,
                id: newId,
              });
              t = true;
            }
            processedIds.push(tocId);
          }
        });
      }
      return t ? a : null;
    },
  });

const getIndexFn = (items: TableOfContentsItem[]): number => {
  const lastItem = items.at(-1);
  return lastItem ? (lastItem.itemIndex || 1) + 1 : 1;
};

const getLevelFn = (node: Node, items: TableOfContentsItem[]): number => {
  let level = 1;
  const lastItem = [...items]
    .reverse()
    .find((item) => item.level <= node.attrs.level);
  const baseLevel = lastItem?.level || 1;
  if (node.attrs.level > (lastItem?.level || 1)) {
    level = (lastItem?.level || 1) + 1;
  } else if (node.attrs.level < (lastItem?.level || 1)) {
    level = baseLevel;
  } else {
    level = lastItem?.level || 1;
  }
  return level;
};

export const tableOfContentsExtension = Extension.create<
  TableOfContentsOptions,
  TableOfContentsStorage
>({
  name: 'tableOfContents',
  addProseMirrorPlugins() {
    return [
      ProseMirrorPlugin({
        getId: this.options.getId,
        anchorTypes: this.options.anchorTypes,
      }),
    ];
  },
  addOptions() {
    return {
      onUpdate: () => {},
      getId: () => uuidv4().substring(0, 6),
      scrollParent: typeof window !== 'undefined' ? window : undefined,
      anchorTypes: ['heading'],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.anchorTypes || ['headline'],
        attributes: {
          id: {
            default: null,
            renderHTML: (e) => ({
              id: e.id,
            }),
            parseHTML: (e) => e.id || null,
          },
          'data-toc-id': {
            default: null,
            renderHTML: (e) => ({
              'data-toc-id': e['data-toc-id'],
            }),
            parseHTML: (e) => e.dataset.tocId || null,
          },
        },
      },
    ];
  },
  onDestroy() {
    if (this.storage.scrollHandler && this.options.scrollParent)
      this.options.scrollParent.removeEventListener(
        'scroll',
        this.storage.scrollHandler,
      );
  },
  addStorage() {
    return {
      content: [],
      anchors: [],
      scrollHandler: () => null,
      scrollPosition: 0,
    };
  },
  onUpdate() {
    updateTableOfContents({
      editor: this.editor,
      storage: this.storage,
      options: this.options,
      getIndex: this.options.getIndex || getIndexFn,
      getLevel: this.options.getLevel || getLevelFn,
    });
  },
  onCreate() {
    const { tr: e } = this.editor.state;
    const o: string[] = [];
    this.editor.state.doc.descendants((s, n) => {
      const a = s.attrs['data-toc-id'];
      if (this.options.anchorTypes.includes(s.type.name)) {
        if (a == null || o.includes(a)) {
          const i = this.options.getId(s.textContent);
          e.setNodeMarkup(n, null, {
            ...s.attrs,
            'data-toc-id': i,
            id: i,
          });
        }
        o.push(a);
      }
    });
    this.editor.view.dispatch(e);

    updateTableOfContents({
      editor: this.editor,
      storage: this.storage,
      options: this.options,
      getIndex: this.options.getIndex || getIndexFn,
      getLevel: this.options.getLevel || getLevelFn,
    });

    this.storage.scrollHandler = () => {
      let scrollPosition;
      const scrollTop =
        // eslint-disable-next-line no-nested-ternary
        this.options.scrollParent instanceof HTMLElement
          ? this.options.scrollParent.scrollTop
          : // eslint-disable-next-line no-cond-assign
            (scrollPosition = this.options.scrollParent) == null
            ? undefined
            : scrollPosition.scrollY;
      this.storage.scrollPosition = scrollTop || 0;
      const updatedContent = updateItems(this.storage.content, {
        editor: this.editor,
        options: this.options,
        storage: this.storage,
      });
      this.storage.content = updatedContent;
    };

    if (this.options.scrollParent) {
      this.options.scrollParent.addEventListener(
        'scroll',
        this.storage.scrollHandler,
      );
    }
  },
  addCommands() {
    return {
      scrollToHeading:
        (id: string) =>
        ({ chain }) => {
          const el = this.editor.view.dom.querySelector(
            `[data-toc-id="${id}"]`,
          );

          if (!el) return chain().run();

          if (!(el instanceof HTMLElement)) return chain().run();
          const element = el as HTMLElement;

          const pos = this.editor.view.posAtDOM(element, 0);
          const { tr } = this.editor.view.state;
          tr.setSelection(new TextSelection(tr.doc.resolve(pos)));
          this.editor.view.dispatch(tr);
          this.editor.view.focus();

          if (!this.options.scrollParent) return chain().run();
          const scrollParent = this.options.scrollParent as HTMLElement;

          const elementOffsetTop = element.offsetTop - scrollParent.offsetTop;

          scrollParent.scrollTo({
            top: elementOffsetTop - 50,
            behavior: 'smooth',
          });

          return chain().run();
        },
    };
  },
});

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    tableOfContents: {
      scrollToHeading: (id: string) => ReturnType;
    };
  }
}
